import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mh, core, useMhStream } from "@/lib/mh";
import { Panel, Eyebrow, Stat, DeptTag } from "@/components/mh/bits";
import { DeptCard } from "@/components/mh/DeptCard";
import { FlowRun } from "@/components/mh/FlowRun";
import { ApprovalCard } from "@/components/mh/ApprovalCard";
import { useLiveRuns, useApprovals } from "@/components/mh/hooks";

const StageLadder = ({ stages }) => (
  <Panel cls="p-5" testId="stage-ladder">
    <Eyebrow>Rollout ladder</Eyebrow>
    <div className="mt-4 space-y-3">
      {stages.map((s, i) => (
        <div key={s.stage}>
          <div className="flex justify-between text-sm"><span><span className="font-mono text-muted-foreground mr-2">0{i + 1}</span>{s.stage}</span><span className="font-mono text-xs text-muted-foreground">{s.live}/{s.total}</span></div>
          <div className="h-1 mt-1.5 rounded-full bg-white/[0.06]"><div className="h-full rounded-full bg-[#D4FF3A] transition-[width] duration-700" style={{ width: `${s.total ? (s.live / s.total) * 100 : 0}%` }} /></div>
        </div>
      ))}
    </div>
  </Panel>
);

const ConnStatus = () => {
  const [c, setC] = useState(null);
  useEffect(() => { core.connections().then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return null;
  const label = (m) => (m === "own" ? "Client's own" : "Managed");
  const done = c.tools.filter((t) => t.connected).length;
  const broke = c.tools.filter((t) => t.broke_since);
  return (
    <div className="flex flex-col gap-2 items-end">
    {broke.length > 0 && (
      <Link to="/connections" data-testid="command-tools-broke" className="font-mono text-[11px] px-3 py-1.5 rounded-full border border-[#FF3B5C]/50 text-[#FF3B5C] bg-[#FF3B5C]/[0.06] hover:bg-[#FF3B5C]/10 transition-colors">
        {broke.length} tool{broke.length === 1 ? "" : "s"} broke: {broke.map((t) => t.name).join(", ")} → fix
      </Link>
    )}
    <Link to="/connections" data-testid="connection-status-card" className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 h-auto py-2.5 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-white/25 transition-colors font-mono text-[11px] text-muted-foreground w-fit">
      <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: c.model.mode === "own" && c.model.status !== "connected" ? "#FF3B5C" : "#D4FF3A" }} />Model: <span className="text-foreground">{label(c.model.mode)}</span></span>
      <span>Runtime: <span className="text-foreground">{label(c.runtime.mode)}</span></span>
      <span><span className="text-foreground">{done} of {c.tools.length}</span> tools connected</span>
    </Link>
    </div>
  );
};

const Signals = ({ signals, deps }) => (
  <Panel cls="p-5" testId="signals-panel">
    <Eyebrow>Signals from command centers</Eyebrow>
    <ul className="mt-3 divide-y divide-white/[0.05]">
      {signals.map((s, i) => (
        <li key={i} className="py-2.5 flex flex-col gap-1">
          <DeptTag name={`${deps[s.department]?.name} · ${s.board}`} hex={deps[s.department]?.hex} />
          <span className="text-sm">{s.text}</span>
        </li>
      ))}
    </ul>
  </Panel>
);

export default function Command() {
  const [data, setData] = useState(null);
  const runs = useLiveRuns(undefined, 14);
  const { items: approvals, total: approvalsTotal, reload } = useApprovals();
  const load = () => mh.overview().then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  useMhStream((ev) => { if (ev.type === "approval.decided" || ev.type === "department.autonomy") load(); });

  if (!data) return <div className="h-[60vh] grid place-items-center font-mono text-xs text-muted-foreground" data-testid="command-loading">Waking the house…</div>;
  const deps = Object.fromEntries(data.departments.map((d) => [d.slug, d]));
  const t = data.totals;

  return (
    <div className="max-w-[1500px] mx-auto space-y-8" data-testid="command-page">
      <header className="mh-rise">
        <div className="flex flex-wrap items-center justify-between gap-3"><Eyebrow>Manahouse · the house, right now</Eyebrow><ConnStatus /></div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.02] max-w-3xl">
          {t.live} operators are working. <em className="text-muted-foreground">{approvalsTotal} are waiting on you.</em>
        </h1>
      </header>

      <Panel cls="p-5 sm:p-7 grid grid-cols-2 lg:grid-cols-5 gap-6 mh-rise" testId="totals-panel">
        <Stat testId="stat-live" label="Agents live" value={t.live} sub={`of ${t.agents} mapped`} accent="#D4FF3A" />
        <Stat testId="stat-runs" label="Runs today" value={t.runs_today.toLocaleString()} sub={`${t.active_runs} in flight`} />
        <Stat testId="stat-approvals" label="Awaiting sign-off" value={approvalsTotal} sub={approvalsTotal > approvals.length ? `showing ${approvals.length} · ${approvalsTotal - approvals.length} more` : "human-in-the-loop"} accent={approvalsTotal ? "#FFB020" : undefined} />
        <Stat testId="stat-cost" label="Model spend" value={`$${Math.round(t.cost_today)}`} sub="today, all depts" />
        <Stat testId="stat-centers" label="Command centers" value={t.command_centers} sub={`${t.in_dev} agents in dev`} />
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-6 min-w-0">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-3xl">The seven</h2>
            <Link to="/departments" className="font-mono text-xs text-muted-foreground hover:text-foreground" data-testid="all-departments-link">All departments →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4">
            {data.departments.map((d, i) => <DeptCard key={d.slug} d={d} i={i} />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StageLadder stages={data.stages} />
            <Signals signals={data.signals.slice(0, 5)} deps={deps} />
          </div>
        </div>

        <aside className="space-y-6 min-w-0">
          <Panel cls="p-5" testId="live-flow-panel">
            <div className="flex items-center justify-between"><Eyebrow>Live flow · every task, every step</Eyebrow><span className="w-2 h-2 rounded-full bg-[#D4FF3A] mh-pulse" /></div>
            <div className="mt-2">{runs.map((r) => <FlowRun key={r.id} run={r} deptHex={deps[r.department]?.hex} compact />)}</div>
          </Panel>
          <Panel cls="p-5" testId="approvals-preview">
            <div className="flex items-center justify-between"><Eyebrow>Your desk</Eyebrow><Link to="/approvals" className="font-mono text-xs text-muted-foreground hover:text-foreground" data-testid="approvals-link">Open queue →</Link></div>
            <div className="mt-3 space-y-3">
              {approvalsTotal === 0 && <p className="text-sm text-muted-foreground py-4">Nothing waiting. The house is humming.</p>}
              {approvals.slice(0, 3).map((a) => <ApprovalCard key={a.id} item={a} dept={deps[a.department]} onDone={reload} />)}
              {approvalsTotal > 3 && <Link to="/approvals" className="font-mono text-[11px] text-muted-foreground hover:text-foreground block pt-1" data-testid="approvals-more-link">+ {approvalsTotal - 3} more waiting →</Link>}
            </div>
          </Panel>
        </aside>
      </div>
    </div>
  );
}
